import { h } from 'preact'

/**
 * AttachmentRow — horizontal row of pending upload chips above the composer.
 *
 * Props:
 *   attachments — array of pending attachment objects:
 *     { localUri, filename, progress, error, signedId, isImage, mimeType }
 *   onRemove(attachment) — callback when user clicks ×
 */
export function AttachmentRow({ attachments, onRemove }) {
  if (!attachments || attachments.length === 0) return null

  return (
    <div class="chat-attachment-chips">
      {attachments.map((att, i) => (
        <AttachmentChip
          key={att._localKey ?? i}
          attachment={att}
          onRemove={() => onRemove(att)}
        />
      ))}
    </div>
  )
}

function AttachmentChip({ attachment, onRemove }) {
  const { localUri, filename, progress, error, signedId, isImage, mimeType } = attachment
  const isDone    = !!signedId && !error
  const isLoading = !isDone && !error

  let chipClass = 'chat-file-chip'
  if (error)  chipClass += ' chat-file-chip--error'
  if (isDone) chipClass += ' chat-file-chip--done'

  const icon = fileIcon(mimeType || '')

  return (
    <div class={chipClass}>
      <div class="chat-file-chip-thumb-wrap">
        {isImage && localUri ? (
          <div class="chat-file-chip-img-wrap">
            <img class="chat-file-chip-thumb" src={localUri} alt={filename} />
            {isLoading && (
              <div class="chat-file-chip-overlay">
                <i class="bi bi-hourglass-split chat-file-chip-hourglass" />
              </div>
            )}
          </div>
        ) : (
          <span class="chat-file-chip-icon">
            <i class={`bi ${error ? 'bi-exclamation-triangle text-danger' : isLoading ? 'bi-hourglass-split text-secondary' : icon}`} />
          </span>
        )}
      </div>

      <div class="chat-file-chip-info">
        <span class="chat-file-chip-name">
          {error ? 'Hiba' : (filename || 'Fájl')}
        </span>
        {isLoading && progress != null && (
          <ProgressRing percent={progress} />
        )}
      </div>

      <button
        type="button"
        class="chat-file-chip-remove"
        aria-label="Eltávolít"
        onClick={onRemove}
      >
        <i class="bi bi-x" />
      </button>
    </div>
  )
}

/** Circular SVG progress indicator. */
function ProgressRing({ percent }) {
  const r   = 7
  const c   = 2 * Math.PI * r
  const pct = Math.min(100, Math.max(0, percent ?? 0))

  return (
    <svg class="chat-file-progress-ring" width={20} height={20}>
      <circle
        r={r}
        cx={10}
        cy={10}
        fill="none"
        stroke="var(--bs-primary, #0d6efd)"
        stroke-width={2}
        stroke-dasharray={`${c}`}
        stroke-dashoffset={`${c - (pct / 100) * c}`}
        transform="rotate(-90 10 10)"
        stroke-linecap="round"
      />
    </svg>
  )
}

function fileIcon(mimeType) {
  if (mimeType.startsWith('image/'))  return 'bi-file-image'
  if (mimeType.startsWith('video/'))  return 'bi-file-play'
  if (mimeType.startsWith('audio/'))  return 'bi-file-music'
  if (mimeType === 'application/pdf') return 'bi-file-earmark-pdf'
  if (mimeType.includes('word'))      return 'bi-file-earmark-word'
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'bi-file-earmark-spreadsheet'
  return 'bi-file-earmark'
}
